import { defineStore } from 'pinia';
import { useRoute } from 'vue-router';

import { useApi } from '@/composables/useApi';

import { ref, watchEffect, computed } from 'vue';
import useDomainsStore from '@/stores/agent/agent-domains';
import useAlertStore from '@/stores/alerts';
import useEmailStore from '@/stores/agent/agent-emails';
import usePaymentStore from '@/stores/agent/agent-payment';
import useStaffStore from '@/stores/agent/agent-staff';
import useQlpStore from '@/stores/agent/agent-qlp';
import useCalltrackingStore from '@/stores/agent/agent-calltracking';
import useBillingStore from '@/stores/agent/agent-billing';
import useAgentLocationsStore from '@/stores/agent/agent-locations';
import useApptSchedulerStore from '@/stores/agent/agent-appt-scheduler';
import useVendorStore from '@/stores/agent/agent-vendors';

export default defineStore('agent', () => {
	const VueRoute = useRoute();

	const domainsStore = useDomainsStore();
	const emailStore = useEmailStore();
	const alertStore = useAlertStore();
	const paymentStore = usePaymentStore();
	const staffStore = useStaffStore();
	const qlpStore = useQlpStore();
	const billingStore = useBillingStore();
	const agentLocationsStore = useAgentLocationsStore();
	const calltrackingStore = useCalltrackingStore();
	const apptSchedulerStore = useApptSchedulerStore();
	const vendorStore = useVendorStore();

	const agent = ref(null);
	const progress = ref(0);
	const tosAcceptedOn = ref(null);
	const services = ref(null);
	const notifications = ref(null);
	const siteNotifications = ref(null);
	const loadingAgentData = ref(false);

	const micrositeUrl = computed(
		() =>
			`https://www.statefarm.com/agent/us/xx/_/sf-${VueRoute?.params?.agentID?.toLowerCase()}`
	);

	const isIntern = computed(() => agent.value?.is_agent_intern);

	async function getDashData(agentID = VueRoute.params?.agentID) {
		const fetcher = useApi(`api/agents/${agentID}/dash/`, {
			message: `Could not retrieve dash data for id ${agentID}! Please try again later.`,
		});
		const result = await fetcher.json();

		tosAcceptedOn.value = result?.data?.value?.tos_accepted_on;
		services.value = result?.data?.value?.services;
	}

	async function getMuidForAgent(agentID) {
		const { data } = await useApi(`api/agents/${agentID}/`, {
			message: 'There was an error getting agent data.',
		}).json();
		return data.value.muid;
	}
	async function doesAgentExist(agentID) {
		const { statusCode } = await useApi(`api/agents/${agentID}/`, {
			message: 'There was an error checking if agent exists.',
		}).head();
		return statusCode.value === 200;
	}

	async function getAgentData(agentID = VueRoute.params?.agentID) {
		if (!agentID) {
			alertStore.addAlert({
				message: `Agent ID is undefined!`,
			});
			return Promise.reject('Agent ID is required but not defined.');
		}

		try {
			progress.value = 0;
			loadingAgentData.value = true;

			await onReset();
			const dashDataPromise = getDashData(agentID);
			const notificationsPromise = reloadNotifications(agentID);

			const fetcher = useApi(`api/agents/${agentID}/`, {
				message: `Could not retrieve data for id ${agentID}! Please try again later.`,
			});

			watchEffect(() => {
				progress.value = fetcher.progress;
			});

			const result = await fetcher.json();

			agent.value = result?.data?.value;

			return Promise.all([dashDataPromise, notificationsPromise]);
		} catch (e) {
			return Promise.reject(e);
		} finally {
			loadingAgentData.value = false;
		}
	}

	async function ensureAgentData() {
		if (agent.value) {
			return Promise.resolve();
		} else {
			try {
				await getAgentData();
				return Promise.resolve();
			} catch (error) {
				console.error(error);
				return Promise.reject();
			}
		}
	}

	async function reloadNotifications(agentID = VueRoute.params?.agentID) {
		try {
			const { data } = await useApi(`api/agents/${agentID}/notifications/`).json();
			notifications.value = data.value;
		} catch {
			alertStore.addAlert({
				message: `We couldn't reload your notifications page! Please try again later.`,
			});
		}
	}

	async function dismissNotification({ note_id }) {
		await useApi(`api/agents/${VueRoute.params.agentID}/notifications/${note_id}/`, {
			message: 'There was an issue dismissing the notification! Please try again later.',
		}).delete();
		await reloadNotifications();
	}

	async function expandNotification({ note_id }) {
		await useApi(`api/agents/${VueRoute.params.agentID}/notifications/${note_id}/expand/`, {
			message: 'There was an issue expanding the notification! Please try again later.',
		}).put();
		await reloadNotifications();
	}

	async function search(searchText) {
		const q = encodeURIComponent(searchText);
		const { data } = await useApi(`api/agents/search/?q=${q}`, {
			message: 'There was an error searching for agents.',
		}).json();
		return data.value.results;
	}

	function onReset() {
		alertStore.$reset();
		billingStore.$reset();
		calltrackingStore.$reset();
		domainsStore.$reset();
		emailStore.$reset();
		paymentStore.$reset();
		qlpStore.$reset();
		staffStore.$reset();
		agentLocationsStore.$reset();
		apptSchedulerStore.$reset();
		vendorStore.$reset();
	}

	return {
		progress,
		agent,
		isIntern,
		tosAcceptedOn,
		services,
		notifications,
		siteNotifications,
		loadingAgentData,
		micrositeUrl,
		search,

		getMuidForAgent,
		doesAgentExist,
		getAgentData,
		getDashData,
		ensureAgentData,
		reloadNotifications,
		dismissNotification,
		expandNotification,
		onReset,
	};
});
